// Central manifest for all local media. Files live under /public/assets/media
// (symlinked from the repo-root assets/). Paths are stored RAW (human-readable,
// with real spaces/special chars). next/image encodes the path itself when
// building the optimizer URL — pre-encoding here would double-encode and 400.
// Plain <video> sources are passed through encodeURI() at the point of use
// (see VideoBox). No external URLs.

function asset(folder: string, file: string): string {
  return `/assets/media/${folder}/${file}`;
}

// Unified media model — the lightbox treats images and videos the same way; only
// `type` decides how a single item renders.
export interface MediaItem {
  type: 'image' | 'video';
  src: string;
}

export const toImageItems = (srcs: string[]): MediaItem[] =>
  srcs.map((src) => ({ type: 'image', src }));

const VIDEO_DIR = 'Videos';
const TILE_DIR = 'Tile';
const PAINTING_DIR = 'Painting & Decorative Finishes';
const ABOUT_DIR = 'About us';
const ELECTRICAL_DIR = 'Electrical & Plumbing (instalacje)';

// Before/after comparison for the Electrical & Plumbing section. Part1 = before
// (clipped top layer, left side), Part2 = after (revealed on the right).
export const electricalComparison = {
  before: asset(ELECTRICAL_DIR, 'Image Comparison Slider (Horizontal)  Part1.webp'),
  after: asset(ELECTRICAL_DIR, 'Image Comparison Slider (Horizontal)  Part2.webp'),
} as const;

// Standalone still shown beside the comparison slider in the Electrical section.
export const electricalImage: string = asset(ELECTRICAL_DIR, 'Electrica .webp');

export const videos = {
  one: asset(VIDEO_DIR, 'o45C4i2BowBAXKHifqyIhQA2IFukZ1Eg3qHKsn.mp4'),
  two: asset(VIDEO_DIR, 'o4GMG6EIQLAQ1L9fEfFI3xLiuCqqYIAbDDfjg0.mp4'),
  three: asset(VIDEO_DIR, 'o8cEo3F6eIDkp1QACLngfoE6nrEqRs8FkBLwAB.mp4'),
  four: asset(VIDEO_DIR, 'ocmA40NsIA16BAGEQhFGfDAagRFO3Pf0lQ2DgE.mp4'),
  five: asset(VIDEO_DIR, 'oECdfABBHyACNiIR0pLiRQyCwITKAYHECA3WiL (1).mp4'),
  six: asset(VIDEO_DIR, 'oMRMfgeA76gRe38PGsg1cjL6XOAMFAeGIyizDe.mp4'),
} as const;

// Lead image first, per CLAUDE.md.
export const tileImages: string[] = [
  'mainimage for tile images must be first.webp',
  '476246513_17843497011422043_5600915931653392238_n.webp',
  '476738961_17843498109422043_7966385763803565410_n.webp',
  '476755167_4828757250681850_5014518658332354104_n.webp',
  '480621189_2025776364557891_776470191854352510_n.webp',
  '480819887_17845203060422043_8114567857610378806_n.webp',
  '491440553_17854671588422043_705900289102598378_n.webp',
  '491445451_1026512975641087_303368744585370903_n.webp',
  '491445803_17855942037422043_4056079496502060240_n.webp',
  '491462939_17855943054422043_7839775126552142464_n.webp',
  '491509973_17855942367422043_4682523337985304631_n.webp',
  '520423367_17866890366422043_7172961875834249343_n.webp',
  '520566741_17866890339422043_6303975779094156805_n.webp',
  '520710803_17866890582422043_8856783868047418507_n.webp',
  '520731369_17866890561422043_3053610069128694770_n.webp',
  '520915693_17866890600422043_8992376091628064475_n.webp',
  '521665033_17866890396422043_2277195755959847110_n.webp',
  'photo_5859262946226147034_y.webp',
  'photo_5859262946226147039_y.webp',
  'photo_5859262946226147042_y.webp',
  'photo_5859262946226147044_y.webp',
  'photo_5888667181392793392_y (1).webp',
  'photo_5888667181392793394_y.webp',
  'photo_5888667181392793395_y.webp',
].map((f) => asset(TILE_DIR, f));

const FLOORS_DIR = 'Floors & Doors (podlogi-drzwi)';

// Doors first, per request.
export const floorsImages: string[] = ['Doors.webp', 'Floor.webp'].map((f) => asset(FLOORS_DIR, f));

export const paintingImages: string[] = [
  '491337508_1216251983357624_4882799315845591181_n.webp',
  '522408399_1873537309899958_3882814275661214422_n.webp',
  'photo_5859262946226147032_y.webp',
  'photo_5859262946226147040_y.webp',
  'photo_5859262946226147041_y.webp',
].map((f) => asset(PAINTING_DIR, f));

export const aboutImage: string = asset(ABOUT_DIR, 'photo_5859262946226147029_y.webp');

const REVIEWS_DIR = 'Reviews';

// Project photos attached to specific Oferteo reviews. Keyed by the review's
// reviewer initial (see the testimonials data in TestimonialsSection). Only the
// reviews that came with photos appear here; the rest render text-only. Files are
// renamed to short, review-aligned slugs (s-/m-/e-) under assets/media/Reviews.
export const reviewImages: Record<string, string[]> = {
  S: ['s-1.webp', 's-2.webp', 's-3.webp'].map((f) => asset(REVIEWS_DIR, f)),
  M: ['m-1.webp', 'm-2.webp'].map((f) => asset(REVIEWS_DIR, f)),
  E: ['e-1.webp', 'e-2.webp'].map((f) => asset(REVIEWS_DIR, f)),
};

// Tile video block: the first clip is the main feature video.
export const tileVideos: string[] = [
  asset(VIDEO_DIR, 'main video.mp4'),
  videos.two,
  videos.three,
  videos.four,
  videos.five,
];
